import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { Label } from '~/eds/src/components/Label/Label';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { slices } from '~/redux/slices';
import Alert from '~/ui/components/Alert/Alert';
import { htmlToText } from '~/utils/strings';

import { CKEditor } from '../../Shared/CKEditor4/CKEditor4';
import {
  CLAUSE_LIBRARY_NAME_CHAR_LIMIT,
  GUIDANCE_NOTES_CHAR_LIMIT,
} from '../constants';

const editorConfig = {
  toolbar: [
    { name: 'editing', items: ['Undo', 'Redo'] },
    {
      name: 'text',
      items: ['Format', 'Bold', 'Italic', 'Underline', 'Strike'],
    },
    { name: 'link', items: ['Link', 'Unlink'] },
    {
      name: 'format',
      items: ['JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock'],
    },
    {
      name: 'paragraph',
      items: ['BulletedList', 'NumberedList', 'BidiLtr', 'BidiRtl'],
    },
  ],
  toolbarGroups: [
    { name: 'group1', groups: ['editing'] },
    { name: 'group2', groups: ['text'] },
    { name: 'group3', groups: ['link'] },
    { name: 'group4', groups: ['format'] },
    { name: 'group5', groups: ['paragraph'] },
  ],
};

const ClauseForm = () => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const dispatch = useDispatch();
  const clauseForm = useSelector(
    slices.clauseLibrary.selectors.selectClauseForm,
  );

  const [clauseName, setClauseName] = useState(clauseForm.name);
  const [guidanceNotes, setGuidanceNotes] = useState(
    clauseForm.guidanceNotes || '',
  );

  const clauseNameFooter = `${
    clauseName.trim().length
  }/${CLAUSE_LIBRARY_NAME_CHAR_LIMIT}`;
  const clauseNameOverLimit =
    clauseName.trim().length > CLAUSE_LIBRARY_NAME_CHAR_LIMIT;
  // must come from validation error because it shouldn't display until submission
  const clauseNameMissing = clauseForm.validationErrors?.find(
    (message) => message === 'clause name missing',
  );
  const clauseNameAPIErrors = clauseForm.errors?.filter(
    (error) => error.source?.pointer === '/name',
  );
  const getClauseNameError = () => {
    if (clauseNameOverLimit)
      return intl.formatMessage(
        {
          id: 'ClauseForm.nameOverLimitError',
          defaultMessage:
            '{footer} (Name cannot be longer than {limit} characters)',
          description:
            'Error message when clause name exceeds character limit. Example: footer = "Character count: 255/200", limit = "200"',
        },
        {
          footer: clauseNameFooter,
          limit: CLAUSE_LIBRARY_NAME_CHAR_LIMIT,
        },
      );
    if (clauseNameMissing)
      return intl.formatMessage({
        id: 'ClauseForm.nameRequiredError',
        defaultMessage: 'This is a required field',
        description: 'Error message when clause name is required',
      });
    if (clauseNameAPIErrors?.length)
      return clauseNameAPIErrors.map((error) => error.detail).join(' ');
    return undefined;
  };

  const guidanceNotesText = htmlToText(guidanceNotes).trim();
  const guidanceNotesTextCharCountDisplay = formatNumber(
    guidanceNotesText.length,
  );
  const guidanceNotesCharLimitDisplay = formatNumber(GUIDANCE_NOTES_CHAR_LIMIT);
  const guidanceNotesFooter = `${guidanceNotesTextCharCountDisplay}/${guidanceNotesCharLimitDisplay}`;
  const guidanceNotesOverLimit =
    guidanceNotesText.length > GUIDANCE_NOTES_CHAR_LIMIT;
  const guidanceNotesAPIErrors = clauseForm.errors?.filter(
    (error) => error.source?.pointer === '/guidance',
  );
  const getGuidanceNotesError = () => {
    if (guidanceNotesOverLimit)
      return intl.formatMessage(
        {
          id: 'ClauseForm.guidanceNotesOverLimitError',
          defaultMessage:
            '{footer} (Guidance notes cannot be longer than {limit} characters)',
          description:
            'Error message when guidance notes exceed character limit. Example: footer = "Character count: 1050/1000", limit = "1000"',
        },
        {
          footer: guidanceNotesFooter,
          limit: guidanceNotesCharLimitDisplay,
        },
      );
    if (guidanceNotesAPIErrors?.length)
      return guidanceNotesAPIErrors.map((error) => error.detail).join(' ');
    return undefined;
  };

  function handleClauseNameUpdate(name: string) {
    setClauseName(name);
    const errors = clauseForm.errors?.filter(
      (error) => error.source?.pointer && error.source.pointer !== '/name',
    );
    const validationErrors = clauseForm.validationErrors?.filter(
      (error) => error !== 'clause name missing',
    );
    dispatch(
      slices.clauseLibrary.actions.setClauseForm({
        ...clauseForm,
        name,
        errors,
        validationErrors,
      }),
    );
  }

  function handleGuidanceNotesUpdate(editor: any) {
    const guidanceNotes = editor.getData();
    setGuidanceNotes(guidanceNotes);
    dispatch(
      slices.clauseLibrary.actions.setClauseGuidanceNotes(guidanceNotes),
    );
    dispatch(slices.clauseLibrary.actions.resetClauseFormGuidanceError());
  }

  function handleDismissErrorAlert() {
    dispatch(
      slices.clauseLibrary.actions.setClauseForm({
        ...clauseForm,
        errors: undefined,
      }),
    );
  }

  const CLAUSE_FORM_POINTERS = ['/name', '/guidance'];

  return (
    <Layout direction="column" spacing={8} w="100%">
      {!!clauseForm?.errors &&
        clauseForm.errors.map((error) => {
          if (
            error.source?.pointer &&
            CLAUSE_FORM_POINTERS?.includes(error.source.pointer)
          )
            return <></>;
          return (
            <Alert
              key="warning"
              enableIcon
              variant="danger"
              onDismiss={handleDismissErrorAlert}
            >
              <Layout direction="column" spacing={2}>
                <Text variant="body">{error.detail}</Text>
              </Layout>
            </Alert>
          );
        })}
      <FormField
        name="clause-library-clause-name"
        label={intl.formatMessage({
          id: 'ClauseForm.clauseNameLabel',
          defaultMessage: 'Clause Name',
          description: 'Label for clause name field',
        })}
        input={TextInput}
        placeholder={intl.formatMessage({
          id: 'ClauseForm.clauseNamePlaceholder',
          defaultMessage: 'Start typing to name the new clause',
          description: 'Placeholder text for clause name input',
        })}
        value={clauseName}
        // @ts-ignore
        onChange={handleClauseNameUpdate}
        footer={clauseNameFooter}
        error={getClauseNameError()}
        required
      />
      <Layout direction="column" spacing={4} aria-label="Guidance">
        <Label>
          {intl.formatMessage({
            id: 'ClauseForm.guidanceLabel',
            defaultMessage: 'Guidance',
            description: 'Label for guidance notes field',
          })}
        </Label>
        <Layout direction="column" spacing={2}>
          <CKEditor
            data={guidanceNotes}
            config={editorConfig}
            onChange={handleGuidanceNotesUpdate}
          />
          {getGuidanceNotesError() ? (
            <Text color="status.danger" variant="tiny" role="note">
              {getGuidanceNotesError()}
            </Text>
          ) : (
            <Text color="text.secondary" variant="tiny" role="note">
              {guidanceNotesFooter}
            </Text>
          )}
        </Layout>
      </Layout>
    </Layout>
  );
};

export default ClauseForm;
